import React, { useEffect, useState } from 'react';
import { Card, Input, Button, Tag, Space, Typography, Spin, Alert, Badge, Modal, Select, message } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  SendOutlined,
  ExperimentOutlined,
  TeamOutlined,
  DeploymentUnitOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import {
  feasibilityApi,
  type FeasibilityReviewType,
  type FeasibilityReviewData,
} from '@/api/feasibilityApi';
import { userApi } from '@/api/userApi';
import { useAuth } from '@/auth/useAuth';
import type { User, UserRole } from '@/types/common';

const { Text, Title } = Typography;

const ROLE_LABELS: Record<UserRole, string> = {
  CEO: 'CEO',
  BD_MANAGER: 'BD Manager',
  ENTERPRISE_ARCHITECT: 'Enterprise Architect',
  CLOUD_ARCHITECT: 'Cloud Architect',
  OPPORTUNITY_FINDER: 'Opportunity Finder',
  REQUIREMENT_RESOURCING_MGR: 'Resourcing Manager',
  TECHNICAL_WRITER: 'Technical Writer',
  QC_LEAD: 'QC Lead',
  CONTRIBUTOR: 'Contributor',
  ADMIN: 'Admin',
};

interface ReviewConfig {
  type: FeasibilityReviewType;
  label: string;
  subtitle: string;
  roles: string[];
  icon: React.ReactNode;
  color: string;
}

const REVIEW_CONFIGS: ReviewConfig[] = [
  {
    type: 'TECHNICAL',
    label: 'B.1 Technical Feasibility',
    subtitle: 'Cloud Architect',
    roles: ['CLOUD_ARCHITECT'],
    icon: <ExperimentOutlined />,
    color: '#1677ff',
  },
  {
    type: 'STAFFING',
    label: 'B.2 Staffing Feasibility',
    subtitle: 'Resourcing Manager',
    roles: ['REQUIREMENT_RESOURCING_MGR'],
    icon: <TeamOutlined />,
    color: '#722ed1',
  },
  {
    type: 'DELIVERY',
    label: 'B.3 Delivery Feasibility',
    subtitle: 'BD Manager / Resourcing Manager',
    roles: ['BD_MANAGER', 'REQUIREMENT_RESOURCING_MGR'],
    icon: <DeploymentUnitOutlined />,
    color: '#13c2c2',
  },
];

interface FeasibilityReviewSectionProps {
  opportunityId: string;
  onComplete: () => void;
}

const FeasibilityReviewSection: React.FC<FeasibilityReviewSectionProps> = ({
  opportunityId,
  onComplete,
}) => {
  const { hasAnyRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<FeasibilityReviewData[]>([]);
  const [remarks, setRemarks] = useState<Record<FeasibilityReviewType, string>>({
    TECHNICAL: '',
    STAFFING: '',
    DELIVERY: '',
  });
  const [submitting, setSubmitting] = useState<FeasibilityReviewType | null>(null);
  const [roleUsers, setRoleUsers] = useState<Record<string, string[]>>({});

  // Reassign state
  const [reassignModalVisible, setReassignModalVisible] = useState(false);
  const [reassignType, setReassignType] = useState<FeasibilityReviewType | null>(null);
  const [reassignUserId, setReassignUserId] = useState<string | undefined>();
  const [reassigning, setReassigning] = useState(false);
  const [allUsers, setAllUsers] = useState<User[]>([]);

  const canReassign = hasAnyRole(['ADMIN', 'CEO', 'BD_MANAGER']);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const data = await feasibilityApi.getReviews(opportunityId);
      setReviews(data);
    } catch {
      // no reviews yet — that's fine
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleUsers = async () => {
    const uniqueRoles = [...new Set(REVIEW_CONFIGS.flatMap((c) => c.roles))];
    const entries = await Promise.all(
      uniqueRoles.map(async (role) => {
        try {
          const users = await userApi.getUserNamesByRole(role as UserRole);
          return [role, users.map((u) => u.fullName)] as const;
        } catch {
          return [role, []] as const;
        }
      }),
    );
    setRoleUsers(Object.fromEntries(entries));
  };

  const fetchAllUsers = async () => {
    try {
      const res = await userApi.getAll({ size: 200, isActive: true });
      setAllUsers(res.content || []);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    fetchReviews();
    fetchRoleUsers();
    fetchAllUsers();
  }, [opportunityId]);

  const getReview = (type: FeasibilityReviewType) =>
    reviews.find((r) => r.reviewType === type);

  const getUserNamesForConfig = (config: ReviewConfig): string[] => {
    const names = new Set<string>();
    for (const role of config.roles) {
      for (const name of roleUsers[role] ?? []) names.add(name);
    }
    return [...names];
  };

  const canSubmit = (config: ReviewConfig): boolean => {
    return hasAnyRole([...config.roles, 'ADMIN', 'CEO']);
  };

  const handleSubmit = async (type: FeasibilityReviewType) => {
    const text = remarks[type].trim();
    if (!text) return;
    setSubmitting(type);
    try {
      await feasibilityApi.submitReview(opportunityId, { reviewType: type, remarks: text });
      await fetchReviews();
      setRemarks((prev) => ({ ...prev, [type]: '' }));
      onComplete();
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to submit review. Please try again.');
    } finally {
      setSubmitting(null);
    }
  };

  const openReassignModal = (type: FeasibilityReviewType) => {
    setReassignType(type);
    setReassignUserId(undefined);
    setReassignModalVisible(true);
  };

  const handleReassign = async () => {
    if (!reassignType || !reassignUserId) {
      message.warning('Please select a user');
      return;
    }
    setReassigning(true);
    try {
      await feasibilityApi.reassignReview(opportunityId, reassignType, reassignUserId);
      message.success('Feasibility review reassigned');
      setReassignModalVisible(false);
      await fetchReviews();
      onComplete();
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to reassign');
    } finally {
      setReassigning(false);
    }
  };

  if (loading) {
    return (
      <Card size="small" style={{ marginTop: 12 }}>
        <Spin size="small" /> Loading feasibility reviews...
      </Card>
    );
  }

  const isSubmitted = (review: FeasibilityReviewData | undefined): boolean =>
    Boolean(review?.submittedAt);

  const completedCount = REVIEW_CONFIGS.filter((c) => isSubmitted(getReview(c.type))).length;
  const allComplete = completedCount === REVIEW_CONFIGS.length;

  return (
    <>
      <Card
        size="small"
        style={{ marginTop: 12, borderColor: allComplete ? '#52c41a' : '#faad14' }}
        title={
          <Space>
            <Text strong style={{ fontSize: 14 }}>
              Feasibility Reviews
            </Text>
            <Badge
              count={`${completedCount} / ${REVIEW_CONFIGS.length}`}
              style={{ backgroundColor: allComplete ? '#52c41a' : '#faad14' }}
            />
          </Space>
        }
      >
        {!allComplete && (
          <Alert
            message="All three feasibility reviews are required to advance to Phase C"
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          {REVIEW_CONFIGS.map((config) => {
            const existing = getReview(config.type);
            const submitted = isSubmitted(existing);
            const assigned = Boolean(existing) && !submitted;

            return (
              <Card
                key={config.type}
                size="small"
                style={{
                  borderLeft: `3px solid ${config.color}`,
                  background: submitted ? 'rgba(34, 197, 94, 0.12)' : assigned ? 'rgba(24, 144, 255, 0.06)' : 'var(--color-bg-container)',
                }}
              >
                <Space style={{ marginBottom: submitted ? 8 : 12, width: '100%', justifyContent: 'space-between' }} align="start">
                  <Space align="start">
                    <span style={{ color: config.color, fontSize: 16 }}>{config.icon}</span>
                    <div>
                      <Text strong style={{ fontSize: 13 }}>
                        {config.label}
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {existing?.reviewerName
                          ? `Assigned to: ${existing.reviewerName}`
                          : config.subtitle}
                        {!existing?.reviewerName && (() => {
                          const names = getUserNamesForConfig(config);
                          return names.length > 0 ? ` (${names.join(', ')})` : '';
                        })()}
                      </Text>
                    </div>
                  </Space>
                  <Space>
                    {submitted ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">Submitted</Tag>
                    ) : assigned ? (
                      <Tag icon={<ClockCircleOutlined />} color="processing">Assigned</Tag>
                    ) : (
                      <Tag icon={<ClockCircleOutlined />} color="warning">Pending</Tag>
                    )}
                    {canReassign && (
                      <Button
                        size="small"
                        icon={<SwapOutlined />}
                        onClick={() => openReassignModal(config.type)}
                      >
                        Reassign
                      </Button>
                    )}
                  </Space>
                </Space>

                {submitted ? (
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Reviewed by {existing!.reviewerName} •{' '}
                      {existing!.submittedAt
                        ? new Date(existing!.submittedAt).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : ''}
                    </Text>
                    <div
                      style={{
                        marginTop: 6,
                        padding: '8px 12px',
                        background: '#e6f4ea',
                        borderRadius: 4,
                        fontSize: 13,
                      }}
                    >
                      {existing!.remarks}
                    </div>
                  </div>
                ) : canSubmit(config) ? (
                  <div>
                    <Input.TextArea
                      value={remarks[config.type]}
                      onChange={(e) =>
                        setRemarks((prev) => ({ ...prev, [config.type]: e.target.value }))
                      }
                      placeholder={`Enter your ${config.label.split(' ').slice(1).join(' ').toLowerCase()} remarks and assessment...`}
                      rows={3}
                      style={{ marginBottom: 8 }}
                    />
                    <div style={{ textAlign: 'right' }}>
                      <Button
                        type="primary"
                        size="small"
                        icon={<SendOutlined />}
                        loading={submitting === config.type}
                        disabled={!remarks[config.type].trim()}
                        onClick={() => handleSubmit(config.type)}
                        style={{ backgroundColor: config.color, borderColor: config.color }}
                      >
                        Submit Review
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                    Awaiting review from {existing?.reviewerName || config.subtitle}
                    {!existing?.reviewerName && (() => {
                      const names = getUserNamesForConfig(config);
                      return names.length > 0 ? ` (${names.join(', ')})` : '';
                    })()}
                  </Text>
                )}
              </Card>
            );
          })}
        </Space>
      </Card>

      {/* Reassign Modal */}
      <Modal
        title={`Reassign ${reassignType ? REVIEW_CONFIGS.find((c) => c.type === reassignType)?.label : ''}`}
        open={reassignModalVisible}
        onCancel={() => setReassignModalVisible(false)}
        onOk={handleReassign}
        confirmLoading={reassigning}
        okText="Reassign"
      >
        <Text style={{ display: 'block', marginBottom: 12 }}>
          Select a user to assign this feasibility review to. All active users are shown with their roles.
        </Text>
        <Select
          style={{ width: '100%' }}
          placeholder="Select a user"
          value={reassignUserId}
          onChange={setReassignUserId}
          showSearch
          optionFilterProp="label"
          options={allUsers.map((u) => ({
            value: u.id,
            label: `${u.fullName} — ${ROLE_LABELS[u.role] || u.role}${u.secondaryRole ? ` / ${ROLE_LABELS[u.secondaryRole] || u.secondaryRole}` : ''}`,
          }))}
        />
      </Modal>
    </>
  );
};

export default FeasibilityReviewSection;
